import {TFunction} from 'i18next';
import {IStateViewer} from './reducer';
import {getCommentCreateDtText} from '../../comment/common';
import {I_Comment} from '../../comment/state/apiGet';
import {ICommentType} from '../../comment/type';
import {Page} from '../../common/constant/page';
import {
  getAdBanner,
  getContentProps,
  getPlatformSource,
  getTorosInfo,
  I_AdBanner,
  I_ContentMeta,
  I_TorosLog,
} from '../../common/state/apiGet';
import {IExternalVideoType, ITorosInfo} from '../../common/type';
import {I_ApiMeta} from '../../common/util/api';
import {toCamelCase} from '../../common/util/string';
import {getEpisodeSubText, I_EpisodeItem} from '../../commonSub/state/apiGet';
import {IEpisodeType, IRecommendItemContent} from '../../commonSub/type';
import {
  IViewerComment,
  IViewerContent,
  IViewerEpisode,
  IViewerVideoClipLocation,
} from '../type';

type I_ViewerContent = I_ContentMeta & {
  scrollView: boolean;
  turningPageView: boolean;
  turningPageDirection: boolean;
  defaultView: string;
};

interface I_ViewerEpisode extends I_EpisodeItem {
  viewerAsset?: {
    bgm?: string;
    externalVideoFrom?: string;
    externalVideoKey?: string;
    externalVideoLocation?: string;
  };
}

export interface I_ViewerImage {
  aid: string;
  codec: string;
  container: string;
  files: [
    {
      height: 0;
      url: string;
      width: 0;
    },
  ];
  totalSize: 0;
  zid: string;
}

type I_ViewerComment = Pick<
  I_Comment,
  | 'id'
  | 'best'
  | 'createdDateTime'
  | 'text'
  | 'type'
  | 'writer'
  | 'spoiling'
  | 'withdraw'
  | 'block'
  | 'writerUserId'
  | 'my'
>;

interface I_RecommendContents {
  id: string;
  title: string;
  contents: I_ContentMeta[];
  torosLog: I_TorosLog;
}

interface I_ViewerLink {
  prev: I_EpisodeItem;
  next: I_EpisodeItem;
  position: {currentOffset: number; lastOffset: number};
}

interface I_EpisodeLike {
  likeCount: number;
}

export function getViewerContentAndEpisode(data: {
  content: I_ViewerContent;
  episode: I_ViewerEpisode;
}) {
  return {
    content: getViewerContent(data.content),
    episode: getViewerEpisode(data.episode),
  };
}

function getViewerEpisode(
  episode: I_ViewerEpisode,
  t?: TFunction
): IViewerEpisode {
  return {
    id: episode.id,
    contentId: episode.contentId,
    sid: episode.seoId,
    title: episode.title,
    ageLimit: episode.ageLimit || 0,
    isStopSalesEpisode: episode.status !== 'SELLING',
    bgm: episode.viewerAsset?.bgm,
    videoClip: getVideoClips(episode.viewerAsset),
    thumbnail: getPlatformSource(episode.asset.thumbnailImage) + '.jpg',
    type: toCamelCase(episode.useType) as IEpisodeType,
    subText: getEpisodeSubText(episode, t),
  };
}

function getVideoClips(asset?: I_ViewerEpisode['viewerAsset']) {
  if (
    asset?.externalVideoFrom &&
    asset.externalVideoKey &&
    asset.externalVideoLocation
  ) {
    return {
      key: asset.externalVideoKey,
      type: toCamelCase(asset.externalVideoFrom) as IExternalVideoType,
      location: toCamelCase(
        asset.externalVideoLocation
      ) as IViewerVideoClipLocation,
    };
  }
}

function getViewerContent(content: I_ViewerContent): IViewerContent {
  const canChangeViewType = content.scrollView && content.turningPageView;
  const defaultViewType = canChangeViewType
    ? content.defaultView === 'TURNING_PAGE'
      ? 'page'
      : 'scroll'
    : content.turningPageView
      ? 'page'
      : 'scroll';

  const _content = getContentProps(content, [
    'id',
    'sid',
    'ageLimit',
    'title',
    'isStopContent',
    'authorDetails',
    'synopsis',
    'badges',
  ]);

  const badge = _content.badges.find(
    badge => badge.imgTextType === 'waitForFreePlus'
  );

  return {
    ..._content,
    badge,
    canChangeViewType,
    pageViewDirection: content.turningPageDirection ? 'forward' : 'backward',
    defaultViewType,
  };
}

export function getViewerResources({
  episodeId,
  media,
  nonce,
  timestamp,
  limit,
}: {
  episodeId: number;
  media: I_ViewerImage;
  nonce: string;
  timestamp: string;
  limit?: number;
}) {
  const image = media;
  const images =
    image.files?.map(image => ({
      encUrl: image.url,
      aspectRatio: image.height / image.width,
    })) ?? [];
  return {
    decryptInfo: {
      episodeId,
      aid: image.aid,
      codec: image.codec,
      container: image.container,
      zid: image.zid,
      nonce,
      timestamp,
    },
    images: limit ? images.slice(0, limit) : images,
  };
}

export function getViewerLink(
  t: TFunction,
  data?: I_ViewerLink
): {
  prevEpisode: IStateViewer['prevEpisode'];
  nextEpisode: IStateViewer['nextEpisode'];
  curEpisodeOffset?: IStateViewer['curEpisodeOffset'];
} {
  return {
    prevEpisode: data?.prev ? getViewerEpisode(data?.prev) : undefined,
    nextEpisode: data?.next ? getViewerEpisode(data?.next, t) : undefined,
    curEpisodeOffset: data?.position.currentOffset,
  };
}

export function getContentLike(
  episodeReview?: I_EpisodeLike,
  myReview?: I_EpisodeLike
) {
  return {
    totalLikeCount: episodeReview?.likeCount || 0,
    myLikeCount: myReview?.likeCount || 0,
  };
}

export function getViewerComment({
  t,
  data,
  meta,
}: {
  t: TFunction;
  data?: {
    commentList?: I_ViewerComment[];
  };
  meta: I_ApiMeta;
}): IViewerComment {
  const now = Date.now();
  return {
    totalCount: meta.pagination?.totalCount ?? 0,
    comments:
      data?.commentList?.map(comment => ({
        id: comment.id,
        userName: comment.writer,
        userId: comment.writerUserId,
        content: comment.text,
        isBest: comment.best,
        isReply: false,
        isSpolier: comment.spoiling,
        isWithdraw: comment.withdraw,
        isBlock: comment.block,
        isMine: comment.my?.mine,
        createDtText: getCommentCreateDtText({
          createdDateTime: comment.createdDateTime,
          now,
          t,
        }),
        type: toCamelCase(comment.type) as ICommentType,
      })) || [],
  };
}

export function getBanners(data?: I_AdBanner[]) {
  const eventBannerData = data?.find(
    item => item.placement === 'ad_event_viewer'
  );
  const extendBannerData = data?.find(
    item => item.placement === 'ad_moment_viewer'
  );

  return {
    eventBanner: eventBannerData
      ? getAdBanner({page: Page.Viewer, data: eventBannerData})
      : undefined,
    extendBanner: extendBannerData
      ? getAdBanner({page: Page.Viewer, data: extendBannerData})
      : undefined,
  };
}

export function getRecommendContents(data?: I_RecommendContents): {
  items: IRecommendItemContent[];
  torosInfo?: ITorosInfo;
} {
  return {
    items:
      data?.contents?.map(content =>
        getContentProps(content, [
          'id',
          'sid',
          'title',
          'titleImg',
          'thumbImg',
          'badges',
          'bgImg',
          'bgColorRgb',
        ])
      ) || [],
    torosInfo: data?.torosLog ? getTorosInfo(data.torosLog) : undefined,
  };
}

export function getContentDecorator(data: I_ContentMeta) {
  /**
   * TODO:
   *   현재 뱃지 정보는 UP, N, 휴재 뱃지 모두 저장하고 있음(앱도 같은 이슈)
   *   문제는 위 뱃지는 동적으로 바뀔 수 있어서 일단 성인 뱃지만 저장하는 걸로 기획과 협의.
   *   나중에 서버에서 내려주는 대로 사용될 예정.
   */
  return getContentProps(
    data,
    [
      'id',
      'sid',
      'title',
      'thumbImg',
      'titleImg',
      'bgColor',
      'bgColorRgb',
      'badges',
      'bgImg',
    ],
    {
      badgesFilter: ({type, title}) => type === 'AGE_LIMIT' && title === '19',
    }
  );
}
